import { CONTRACT_TYPE_PRICING } from '@/types/admin';
import type { ClientGrade, ContractType, PaymentType } from '@/types/contracts';

const GRADE_T_PRICING = { one_time: 1, subscription: 1, monthly: 0.1 } as const;

export interface PaymentTypePricingMessages {
  option: string;
  hint: string;
  recommendation: string;
}

export function getContractTypeFromRaw(value: unknown): ContractType {
  const normalized = String(value || 'individual').trim().toLowerCase();
  if (normalized === 'duo' || normalized === 'family' || normalized === 'linked') {
    return normalized;
  }
  return 'individual';
}

/** Etiqueta corta para mostrar en UI según tipo de pago */
export function getPaymentTypeLabel(paymentType?: PaymentType | null): string {
  if (paymentType === 'subscription') return 'pago mensual';
  if (paymentType === 'one_time_full') return 'pago único';
  return 'pago en 2 plazos';
}

/** Tooltip/título para tipo de pago (accesibilidad) */
export function getPaymentTypeTitle(paymentType?: PaymentType | null): string {
  if (paymentType === 'subscription') return 'Plan en 10 cuotas mensuales (aplazada)';
  if (paymentType === 'one_time_full') return 'Pago único al contratar';
  return 'Oferta única: 50% al contratar y 50% tras la comunicación favorable';
}

export function getPricingByContract(grade?: ClientGrade, contractType?: ContractType) {
  if (grade === 'T') return GRADE_T_PRICING;
  const safeType = contractType || 'individual';
  return CONTRACT_TYPE_PRICING[safeType];
}

export function getExpectedAmountByPaymentType(
  paymentType: PaymentType,
  grade?: ClientGrade,
  contractType?: ContractType
): number {
  const pricing = getPricingByContract(grade, contractType);
  // one_time_full usa el mismo importe que one_time (pago único completo)
  const key = paymentType === 'one_time_full' ? 'one_time' : paymentType;
  return pricing[key as keyof typeof pricing];
}

export function getPaymentTypeMessages(
  paymentType: PaymentType,
  grade?: ClientGrade,
  contractType?: ContractType
): PaymentTypePricingMessages {
  const pricing = getPricingByContract(grade, contractType);
  const contractLabel = contractType === 'linked' ? 'contrato 2x1' : `grado ${grade || 'A'}`;

  if (paymentType === 'one_time') {
    const total = pricing.one_time;
    const half = total / 2;
    return {
      option: `Pago en 2 plazos (${half}€ + ${half}€ = ${total}€ total)`,
      hint: `Pago en 2 plazos: ${half}€ + ${half}€ = ${total}€ total`,
      recommendation: `Para ${contractLabel} con pago en 2 plazos, el importe debería ser ${total}€ (${half}€ + ${half}€)`,
    };
  }

  if (paymentType === 'one_time_full') {
    return {
      option: 'Pago único (100%)',
      hint: 'Un solo cargo por el importe total',
      recommendation: 'Importe libre en EUR (ej. 195, 295, 390).',
    };
  }

  const total = pricing.subscription;
  const monthly = pricing.monthly;
  return {
    option: `Pago mensual (10 cuotas de ${monthly}€ = ${total}€ total)`,
    hint: `Pago mensual: 10 cuotas de ${monthly}€ = ${total}€ total`,
    recommendation: `Para ${contractLabel} con pago mensual, el importe debería ser ${total}€ (10 cuotas de ${monthly}€)`,
  };
}

/**
 * Parser tolerante para inputs de importe en formatos ES/EU:
 * - "400", "400.50", "400,50", "1.234,56", "€ 1,234.56"
 */
export function parseContractAmountInput(rawValue: unknown): number | null {
  if (typeof rawValue === 'number') {
    return Number.isFinite(rawValue) ? rawValue : null;
  }

  if (rawValue === null || rawValue === undefined) return null;

  let normalized = String(rawValue).trim();
  if (!normalized) return null;

  normalized = normalized.replace(/[^\d,.-]/g, '');
  if (!normalized) return null;

  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    // Si la coma aparece después del punto, asumimos formato ES: 1.234,56
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato EN: 1,234.56
      normalized = normalized.replace(/,/g, '');
    }
  } else if (hasComma) {
    normalized = normalized.replace(',', '.');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isAmountMatchingExpected(
  amount: number | null,
  expected: number,
  tolerance = 0.009
): boolean {
  if (amount === null) return false;
  return Math.abs(amount - expected) <= tolerance;
}
